/* eslint-disable @typescript-eslint/no-non-null-assertion */
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { 
    AllCommunityModule,
    CellSelectionOptions,
    CellValueChangedEvent,
    ColDef,
    ModuleRegistry,
    ProcessDataFromClipboardParams,
    provideGlobalGridOptions
} from 'ag-grid-community';
import { 
    CellSelectionModule,
    ClipboardModule,
    ExcelExportModule
} from 'ag-grid-enterprise';
import { Button } from '@mui/material';
import { CCSpinner } from '../cc-spinner';
import { SimpleModal } from '../simple-modal';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

ModuleRegistry.registerModules([
    AllCommunityModule,
    CellSelectionModule,
    ClipboardModule,
    ExcelExportModule
]);
// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: 'legacy'});

export interface ValidAgGridRowModel {
    [key: string | symbol]: string | number | boolean;
}

export interface ValidGridRefData {
    [key: string | symbol]: string;
}
  
const styles = (theme: Theme) =>
    createStyles({
        root: {
            position: 'relative'
        },
        gridContainer: {
            width: '100%',
            height: '100%'
        },
        spinner: {
            position: 'absolute',
            backgroundColor: theme.ccPalette.disabled.light,
            width: '100%',
            height: '100%',
            zIndex: 1,
            left: 0,
            top: 0
        },
        modal: {
            position: 'absolute',
            padding: '3em',
            whiteSpace: 'pre-wrap'
        }
    });

interface Props extends WithStyles<typeof styles> {
    className?: string;
    isLoading?: boolean;
    isDisabledSaveButton?: boolean;
    isDisabledEditMode?: boolean;
    startAsEditableMode?: boolean;
    saveModalMessage?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cellSelectionOptions?: CellSelectionOptions<any>;
    rows: ValidAgGridRowModel[];
    columns: ColDef[];
    onGridSaved?: (rows: ValidAgGridRowModel[]) => void;
    onGridExport?: () => void;
    onGridChanged?: (params: CellValueChangedEvent, saveGridModeCallback: () => void) => void;
    validateClipboardData?: (params: ProcessDataFromClipboardParams) => string[][] | null;
}

const AgGridList: React.FC<Props & AgGridReactProps> = (props) => {
    const {
        classes,
        className,
        saveModalMessage,
        rows: initialRows,
        columns: initialColumns,
        isLoading,
        isDisabledSaveButton,
        isDisabledEditMode,
        startAsEditableMode,
        cellSelectionOptions,
        onGridSaved,
        onGridExport,
        onGridChanged,
        validateClipboardData,
        ...AgGridReactProps
    } = props;

    const gridRef = React.useRef<AgGridReact<ValidAgGridRowModel>>(null);
    const rootClasses = `${classes.root}${className ? ` ${className}`: ''}`;
    const gridClasses = `${classes.gridContainer} ag-theme-quartz`;

    const [rowData, setRowData] = React.useState<ValidAgGridRowModel[]>([]);
    const [isEditMode, setEditMode] = React.useState(false);
    const [openModal, setOpenModal] = React.useState(false);
    const [colDefs, setColDefs] = React.useState<ColDef[]>(initialColumns);

    const defaultColDef = React.useMemo<ColDef>(() => {
        return {
            flex: 1
        };
    }, []);

    const editGridMode = () => {
        const columns = [...colDefs];
        columns.forEach(e => {
            if (e.cellEditor) {
                e.editable = true;
            }
        });
        gridRef.current!.api.setGridOption('columnDefs', columns);
        setEditMode(true);
    };

    const saveGridMode = () => {
        const columns = [...colDefs];
        columns.forEach(e => {
            if (e.cellEditor) {
                e.editable = false;
            }
        });
        gridRef.current!.api.setGridOption('columnDefs', columns);
        setEditMode(false);
    };

    const onSaveGridChanges = () => {
        setOpenModal(true);
    };

    const onModalResult = (type: number) => {
        if (onGridSaved && type === 1) {
            saveGridMode();
            onGridSaved(rowData);
        } else {
            editGridMode();
        }

        setOpenModal(false);
    };

    const onCellValueChanged = (params: CellValueChangedEvent) => {
        if (onGridChanged) {
            onGridChanged(params, saveGridMode);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cellSelection: CellSelectionOptions<any> | undefined = React.useMemo(() => { 
        return cellSelectionOptions;
    }, []);

    const onExport = React.useCallback(() => {
        if (onGridExport) {
            onGridExport();
            return;
        }

        gridRef.current!.api.exportDataAsExcel();
    }, []);

    React.useEffect(() => {
        setRowData(initialRows);
    }, [initialRows]);

    React.useEffect(() => {
        setColDefs(initialColumns);
        setEditMode(false);

        if (startAsEditableMode) {
            setTimeout(editGridMode, 500);
        }
    }, [initialColumns]);

    return (
        <div className={rootClasses}>
            <CCSpinner
                className={classes.spinner}
                loading={isLoading as boolean}
                size={70}
            />
            <div>
                {   onGridSaved &&
                    <>
                        <Button
                            color="primary"
                            startIcon={isEditMode ? <CancelIcon/> : <EditIcon />}
                            disabled={isDisabledEditMode}
                            onClick={isEditMode ? saveGridMode : editGridMode}
                        >
                            { isEditMode ? 'Close Edit' : 'Edit Grid' }
                        </Button>
                        <Button
                            color="primary"
                            startIcon={<SaveIcon />}
                            disabled={isDisabledSaveButton}
                            onClick={onSaveGridChanges}
                        >
                            Save grid changes
                        </Button>
                    </>
                }
                <Button
                    color="primary"
                    startIcon={<ExitToAppIcon />}
                    onClick={onExport}
                >
                    Export to Excel
                </Button>
            </div>
            <div
                className={gridClasses}
            >
                <AgGridReact
                    {...AgGridReactProps}
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    // Enabled for multiple cells support
                    cellSelection={cellSelection}
                    // Disabled to avoid data removal
                    suppressCutToClipboard={true}
                    processDataFromClipboard={validateClipboardData}
                    onCellValueChanged={onCellValueChanged}
                />
            </div>
            <SimpleModal
                className={classes.modal}
                open={openModal}
                onModalResult={onModalResult}
                buttonOkLabel="Save"
            >
                { saveModalMessage }
            </SimpleModal>
        </div>
    );
};

AgGridList.defaultProps = {
    isLoading: false,
    startAsEditableMode: false,
    cellSelectionOptions: {},
    isDisabledSaveButton: false,
    isDisabledEditMode: false,
    saveModalMessage: 'Are you sure, to save changes?'
};

const MUIComponent = withStyles(styles)(AgGridList);
export { MUIComponent as AgGridList };
